<template>
  <div class="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-6">
    <h3 class="text-sm font-medium text-gray-900 dark:text-gray-100 mb-4">Site Health</h3>

    <div v-if="loading" class="animate-pulse space-y-2">
      <div class="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/2" />
      <div class="h-2 bg-gray-200 dark:bg-gray-700 rounded w-full" />
    </div>

    <div v-else-if="summary">
      <!-- Score bar -->
      <div v-if="summary.average_score != null" class="mb-4">
        <div class="flex items-center justify-between mb-1">
          <span class="text-2xl font-bold" :class="scoreColor">{{ summary.average_score }}%</span>
          <span class="text-xs text-gray-500 dark:text-gray-400">avg score</span>
        </div>
        <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
          <div class="h-2 rounded-full transition-all" :class="barColor"
            :style="{ width: `${summary.average_score}%` }"></div>
        </div>
      </div>

      <!-- Stats -->
      <dl class="space-y-2 mb-4">
        <div class="flex justify-between">
          <dt class="text-xs text-gray-500 dark:text-gray-400">Pages audited</dt>
          <dd class="text-xs font-medium text-gray-900 dark:text-gray-100">
            {{ summary.audited_pages }} / {{ summary.total_pages }}
          </dd>
        </div>
        <div v-if="summary.issues.critical > 0" class="flex justify-between">
          <dt class="text-xs text-red-600 dark:text-red-400">Critical issues</dt>
          <dd class="text-xs font-medium text-red-600 dark:text-red-400">{{ summary.issues.critical }}</dd>
        </div>
        <div v-if="summary.issues.high > 0" class="flex justify-between">
          <dt class="text-xs text-amber-600 dark:text-amber-400">High issues</dt>
          <dd class="text-xs font-medium text-amber-600 dark:text-amber-400">{{ summary.issues.high }}</dd>
        </div>
        <div v-if="summary.issues.medium > 0" class="flex justify-between">
          <dt class="text-xs text-gray-600 dark:text-gray-400">Medium issues</dt>
          <dd class="text-xs font-medium text-gray-600 dark:text-gray-300">{{ summary.issues.medium }}</dd>
        </div>
      </dl>

      <!-- Bulk audit button -->
      <button @click="handleBulkAudit" :disabled="bulkRunning"
        class="btn btn-secondary btn-sm w-full">
        {{ bulkRunning ? 'Auditing...' : 'Audit All Pages' }}
      </button>

      <!-- Bulk progress -->
      <div v-if="bulkRunning && bulkProgress" class="mt-3">
        <div class="flex justify-between text-xs text-gray-500 dark:text-gray-400 mb-1">
          <span>{{ bulkProgress.current_page || 'Starting...' }}</span>
          <span>{{ bulkProgress.completed }}/{{ bulkProgress.total }}</span>
        </div>
        <div class="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
          <div class="bg-primary-500 h-1.5 rounded-full transition-all duration-300"
            :style="{ width: `${(bulkProgress.completed / bulkProgress.total) * 100}%` }"></div>
        </div>
      </div>
    </div>

    <div v-else class="text-xs text-gray-500 dark:text-gray-400 text-center py-2">
      No audit data yet.
      <button @click="handleBulkAudit" :disabled="bulkRunning" class="text-primary-600 dark:text-primary-400 hover:underline">
        Run first audit
      </button>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue'
import { getAuditSummary, bulkAudit } from '~/javascript/api/page_audits'
import consumer from '~/channels/consumer'

const props = defineProps({
  websiteId: { type: String, required: true }
})

const emit = defineEmits(['bulk-started', 'bulk-completed'])

const loading = ref(true)
const summary = ref(null)
const bulkRunning = ref(false)
const bulkProgress = ref(null)
let subscription = null
let pollTimer = null

const scoreColor = computed(() => {
  const s = summary.value?.average_score
  if (s >= 80) return 'text-green-600 dark:text-green-400'
  if (s >= 50) return 'text-amber-600 dark:text-amber-400'
  return 'text-red-600 dark:text-red-400'
})

const barColor = computed(() => {
  const s = summary.value?.average_score
  if (s >= 80) return 'bg-green-500'
  if (s >= 50) return 'bg-amber-500'
  return 'bg-red-500'
})

async function fetchSummary() {
  try {
    loading.value = true
    const { data } = await getAuditSummary(props.websiteId)
    summary.value = data
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to fetch audit summary:', e)
  } finally {
    loading.value = false
  }
}

async function handleBulkAudit() {
  bulkRunning.value = true
  bulkProgress.value = null
  emit('bulk-started')
  try {
    await bulkAudit(props.websiteId)
    startPolling()
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Bulk audit failed:', e)
    bulkRunning.value = false
  }
}

function startPolling() {
  stopPolling()
  pollTimer = setInterval(async () => {
    try {
      const { data } = await getAuditSummary(props.websiteId)
      // If we have summary and no bulk is apparently running (audited pages didn't change for a while),
      // check if bulk completed by comparing counts
      if (data && !bulkRunning.value) {
        stopPolling()
        return
      }
      if (data) {
        summary.value = data
        // If all pages audited, bulk is done
        if (data.audited_pages >= data.total_pages) {
          bulkRunning.value = false
          bulkProgress.value = null
          stopPolling()
          emit('bulk-completed')
        }
      }
    } catch (_e) { /* ignore polling errors */ }
  }, 5000)
}

function stopPolling() {
  if (pollTimer) {
    clearInterval(pollTimer)
    pollTimer = null
  }
}

function subscribeToChannel() {
  subscription = consumer.subscriptions.create(
    { channel: 'BulkAuditChannel', website_id: props.websiteId },
    {
      received(data) {
        if (data.event === 'page_completed' || data.event === 'page_failed') {
          bulkProgress.value = { completed: data.completed, total: data.total, current_page: data.page_title }
        }
        if (data.event === 'bulk_completed') {
          bulkRunning.value = false
          bulkProgress.value = null
          stopPolling()
          fetchSummary()
          emit('bulk-completed')
        }
      }
    }
  )
}

const refresh = () => fetchSummary()
defineExpose({ refresh })

onMounted(() => {
  fetchSummary()
  subscribeToChannel()
})

onUnmounted(() => {
  subscription?.unsubscribe()
  stopPolling()
})
</script>
